import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';

import { Modal, Button, Heading } from '@simplywallst/ui-core/next';

import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';

import { ROUTE_PORTFOLIO_TRANSACTIONS_EDIT_HOLDING } from '@/constants/routes';

import type { InvalidHolding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import { CompanyIcon } from '@/components/CompanyIcon';

import { pluralise } from '@/utilities/pluralise';

export interface Props {
  portfolioId: Portfolio['id'];
  invalidHoldings: InvalidHolding[];
  onClose: () => void;
}

export const InvalidHoldingsModal = ({
  portfolioId,
  invalidHoldings,
  onClose,
}: Props) => {
  const dispatch = useDispatch();

  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'InvalidHoldingsModal',
  });

  return (
    <Modal
      title={
        <span className="grid grid-cols-[auto,1fr] items-center gap-x-2">
          <svg width="40" height="40" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="6" fill="#FFF" />
            <WarningSolid className="fill-warning" />
          </svg>
          <Heading as="h2" className="text-xl">
            Review and fix portfolio issues
          </Heading>
        </span>
      }
      isOpen
      onClose={onClose}
      className="grid h-screen max-w-[700px] grid-rows-[max-content_1fr] md:h-[600px]"
    >
      <div className="grid grid-rows-[max-content_1fr] rounded-b-x1_5 bg-surface-2 py-x3 lg:mx-x2 lg:mb-x2 lg:rounded-t-x1_5">
        <div className="px-x3">
          <Heading as="h3" className="mb-x1 text-sm">
            {`${invalidHoldings.length} invalid ${pluralise(
              invalidHoldings.length,
              'holding',
              'holdings'
            )}: Negative amount of shares`}
          </Heading>
          <p className="text-xs text-soft">
            Holdings with negative shares are excluded from your portfolio.
            Review your transactions to ensure the total is positive (if you
            still own the stock) or 0 (if fully liquidated).
          </p>
        </div>
        <div className="relative">
          <div className="scrollbar absolute size-full overflow-y-auto overflow-x-hidden px-x3">
            <ul role="list">
              {invalidHoldings.map((n) => (
                <li
                  key={`${n.symbol}-${n.units}`}
                  role="listitem"
                  className="grid grid-rows-[auto_auto] items-center gap-x-x1 border-t border-strong py-x1 pl-x2 last:pb-0 sm:grid-cols-[1fr_1fr] sm:grid-rows-[auto]"
                >
                  <div className="grid grid-cols-[32px_auto] items-center gap-x1">
                    <CompanyIcon
                      tickerSymbol={n.symbol}
                      website={n.company.info?.url}
                      size={32}
                    />
                    <div className="grid grid-rows-[auto_auto]">
                      <span className="line-clamp-1 text-ellipsis text-sm">
                        {n.company.name}
                      </span>
                      <span className="text-xs text-soft">
                        {n.company.uniqueSymbol}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-[1fr_max-content] items-center gap-x1">
                    <div className="text-sm text-bad">{n.units} shares</div>
                    <Button
                      variant="outline"
                      size="medium"
                      onClick={() => {
                        trackEvent({
                          modifier: 'Fix now',
                          action: 'click',
                        });
                        dispatch({
                          type: ROUTE_PORTFOLIO_TRANSACTIONS_EDIT_HOLDING,
                          payload: {
                            portfolioId,
                            holdingId: n.id,
                          },
                        });
                      }}
                    >
                      Fix now <ChevronRightSmallSolid className="-mr-x1" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};
